// Payer Rules Engine - Ruleset Types (Day One Spec)

// Enums matching database
export type PayerType = 'BWC' | 'COMMERCIAL' | 'MEDICARE' | 'OTHER';
export type RuleType = 
  | 'AUTH_REQUIREMENT'
  | 'DOC_REQUIREMENT'
  | 'SUBMISSION_LANE'
  | 'SLA_TIMER'
  | 'FOLLOW_UP_CADENCE'
  | 'SPECIAL_CONDITIONS'
  | 'EXCLUSIONS';

export type RulesetStatus = 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
export type RuleConfidence = 'TEMPLATE' | 'VERIFIED' | 'LEARNED';
export type AuthRequired = 'YES' | 'NO' | 'CONDITIONAL' | 'UNKNOWN';
export type SubmissionLane = 'FHIR_PAS' | 'PORTAL' | 'FAX_PHONE';

// Payer entity
export interface Payer {
  id: string;
  payer_id: string;
  name: string;
  payer_type: PayerType;
  submission_lane_priority: string[];
  portal_url?: string;
  phone?: string;
  fax?: string;
  notes_internal?: string;
  avg_days_to_decision?: number;
  epa_enabled?: boolean;
  created_at: string;
}

// Plan entity
export interface Plan {
  id: string;
  payer_id: string;
  plan_name: string;
  state?: string;
  is_default_plan: boolean;
  created_at: string;
  updated_at: string;
  // Joined
  payer?: Payer;
}

// RuleSet entity
export interface RuleSet {
  id: string;
  payer_id: string;
  plan_id?: string;
  jurisdiction?: string;
  effective_start_date: string;
  effective_end_date?: string;
  version: number;
  status: RulesetStatus;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  // Joined
  payer?: Payer;
  plan?: Plan;
  rules?: Rule[];
}

// Rule match criteria structures
export interface AuthRequirementCriteria {
  cpt_codes?: string[];
  cpt_range?: { start: string; end: string };
  modifiers?: string[];
  site_of_service?: ('hospital' | 'asc' | 'office')[];
  diagnosis_patterns?: string[];
  injury_type?: 'bwc' | 'commercial';
}

export interface DocRequirementCriteria {
  cpt_codes?: string[];
  auth_required?: AuthRequired;
}

export interface SubmissionLaneCriteria {
  payer_type?: PayerType;
  epa_enabled?: boolean;
}

export interface SLATimerCriteria {
  urgency?: 'routine' | 'urgent' | 'emergent';
  payer_type?: PayerType;
}

// Rule decision structures
export interface AuthRequirementDecision {
  auth_required: AuthRequired;
  condition_description?: string;
}

export interface DocRequirementDecision {
  required_documents: string[];
  conditional_documents?: Array<{
    document_code: string;
    condition: string;
  }>;
}

export interface SubmissionLaneDecision {
  primary_lane: SubmissionLane;
  fallback_lane?: SubmissionLane;
}

export interface SLATimerDecision {
  sla_hours: number;
  urgent_sla_hours?: number;
}

export interface FollowUpCadenceDecision {
  initial_follow_up_hours: number;
  follow_up_cadence_hours: number;
}

export interface SpecialConditionsDecision {
  instructions: string[];
  clinical_criteria?: string;
}

export interface ExclusionsDecision {
  excluded_cpt_codes?: string[];
  exclusion_reason: string;
}

// Rule entity
export interface Rule {
  id: string;
  ruleset_id: string;
  rule_type: RuleType;
  match_criteria: Record<string, unknown>;
  decision: Record<string, unknown>;
  priority: number;
  confidence: RuleConfidence;
  is_active: boolean;
  notes?: string;
  last_updated_by?: string;
  last_updated_at: string;
  created_at: string;
}

// Authorization Case (enhanced authorization_requests)
export interface AuthorizationCase {
  id: string;
  case_number: string;
  patient_id: string;
  surgeon_id: string;
  payer_id: string;
  plan_id?: string;
  facility_id?: string;
  
  // Procedure details
  cpt_codes: string[];
  modifiers?: string[];
  icd10_codes: string[];
  diagnosis_summary: string;
  procedure_description: string;
  scheduled_date?: string;
  urgency?: 'routine' | 'urgent' | 'emergent';
  
  // Rules engine results
  auth_required: AuthRequired;
  required_docs: string[];
  submission_lane?: SubmissionLane;
  ruleset_id?: string;
  matched_rule_ids?: string[];
  
  // SLA & Follow-up
  sla_deadline?: string;
  follow_up_schedule?: FollowUpEntry[];
  
  // Status
  status: string;
  created_at: string;
  updated_at: string;
}

export interface FollowUpEntry {
  scheduled_at: string;
  type: 'call' | 'portal_check' | 'fax' | 'email';
  completed: boolean;
  completed_at?: string;
  notes?: string;
}

// Display labels
export const PAYER_TYPE_LABELS: Record<PayerType, string> = {
  BWC: 'Workers\' Comp (BWC)',
  COMMERCIAL: 'Commercial',
  MEDICARE: 'Medicare',
  OTHER: 'Other',
};

export const RULE_TYPE_LABELS: Record<RuleType, string> = {
  AUTH_REQUIREMENT: 'Authorization Requirement',
  DOC_REQUIREMENT: 'Documentation Requirement',
  SUBMISSION_LANE: 'Submission Lane',
  SLA_TIMER: 'SLA Timer',
  FOLLOW_UP_CADENCE: 'Follow-up Cadence',
  SPECIAL_CONDITIONS: 'Special Conditions',
  EXCLUSIONS: 'Exclusions',
};

export const RULESET_STATUS_LABELS: Record<RulesetStatus, string> = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  ARCHIVED: 'Archived',
};

export const RULESET_STATUS_COLORS: Record<RulesetStatus, string> = {
  DRAFT: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  ACTIVE: 'bg-primary/10 text-primary border-primary/30',
  ARCHIVED: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
};

export const AUTH_REQUIRED_LABELS: Record<AuthRequired, string> = {
  YES: 'Required',
  NO: 'Not Required',
  CONDITIONAL: 'Conditional',
  UNKNOWN: 'Unknown',
};

export const AUTH_REQUIRED_COLORS: Record<AuthRequired, string> = {
  YES: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  NO: 'bg-primary/10 text-primary border-primary/30',
  CONDITIONAL: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  UNKNOWN: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
};

export const SUBMISSION_LANE_LABELS: Record<SubmissionLane, string> = {
  FHIR_PAS: 'FHIR PAS (Electronic)',
  PORTAL: 'Payer Portal',
  FAX_PHONE: 'Fax/Phone',
};

export const CONFIDENCE_LABELS: Record<RuleConfidence, string> = {
  TEMPLATE: 'Template',
  VERIFIED: 'Verified',
  LEARNED: 'Learned',
};

export const CONFIDENCE_COLORS: Record<RuleConfidence, string> = {
  TEMPLATE: 'bg-slate-500/10 text-slate-400',
  VERIFIED: 'bg-primary/10 text-primary',
  LEARNED: 'bg-purple-500/10 text-purple-500',
};
